import { useEffect, useId, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Paintbrush } from 'lucide-react';
import { useCms } from './CmsContext';
import { adminApi } from '../../lib/adminApi';
import { sugarloafPlaceholderDogs } from '../../content/defaultSiteContent';
import { isFacebookSectionVisible, normalizeFacebookUrl, setFacebookSectionVisibility, SUGARLOAF_FACEBOOK_PAGE_URL } from '../../content/facebook';
import type { FacebookEmbed, LineageDog, SiteContent as SiteContentType } from '../../content/types';
import { MediaModal } from '../editor/MediaModal';

type Section = 'hero' | 'copy' | 'dogs' | 'facebook' | 'health' | 'faq';
const SECTIONS: Array<[Section, string, string]> = [
  ['hero', 'Hero', '/#top'],
  ['copy', 'Section copy', '/#dogs'],
  ['dogs', 'Our dogs', '/#dogs'],
  ['facebook', 'Facebook', '/#facebook'],
  ['health', 'Health', '/#health'],
  ['faq', 'FAQ', '/#faq'],
];

const emptyPedigree = () => ({ sire: '', sireGrands: [], dam: '', damGrands: [] });

function copyDog(dog: LineageDog): LineageDog {
  return {
    ...dog,
    image: { ...dog.image },
    health: [...dog.health],
    awards: [...dog.awards],
    pedigree: dog.pedigree ? {
      ...dog.pedigree,
      sireGrands: [...dog.pedigree.sireGrands],
      damGrands: [...dog.pedigree.damGrands],
    } : undefined,
  };
}

function uniqueDogId(base: string, dogs: LineageDog[]) {
  const slug = base.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'dog';
  if (!dogs.some((dog) => dog.id === slug)) return slug;
  let suffix = 2;
  while (dogs.some((dog) => dog.id === `${slug}-${suffix}`)) suffix += 1;
  return `${slug}-${suffix}`;
}

function moveDog(dogs: LineageDog[], index: number, offset: -1 | 1) {
  const destination = index + offset;
  if (index < 0 || destination < 0 || destination >= dogs.length) return dogs;
  const next = [...dogs];
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
}

const cleanLines = (items: string[]) => items.map((item) => item.trim()).filter(Boolean);

export function SiteContent() {
  const { content, setContent, resetContent } = useCms();
  const [section, setSection] = useState<Section>('hero');
  const [uploadNote, setUploadNote] = useState('');
  const [selectedDogId, setSelectedDogId] = useState(content.lineage[0]?.id ?? '');
  const [deleteDogId, setDeleteDogId] = useState<string | null>(null);
  const [mediaDogId, setMediaDogId] = useState<string | null>(null);
  const [removeFacebookId, setRemoveFacebookId] = useState<string | null>(null);

  const patch = (updater: (current: SiteContentType) => SiteContentType) => setContent(updater);
  const selectedDog = content.lineage.find((dog) => dog.id === selectedDogId) ?? content.lineage[0] ?? null;
  const selectedDogIndex = selectedDog ? content.lineage.findIndex((dog) => dog.id === selectedDog.id) : -1;
  const missingNamedDogs = sugarloafPlaceholderDogs.filter((template) => !content.lineage.some((dog) => dog.id === template.id || dog.name.trim().toLowerCase() === template.name.toLowerCase()));

  useEffect(() => {
    if (selectedDog && selectedDog.id !== selectedDogId) setSelectedDogId(selectedDog.id);
    if (!selectedDog) setSelectedDogId('');
  }, [selectedDog, selectedDogId]);

  const updateDog = (dogId: string, updater: (dog: LineageDog) => LineageDog) => {
    patch((current) => ({ ...current, lineage: current.lineage.map((dog) => (dog.id === dogId ? updater(dog) : dog)) }));
  };

  const addDog = (template?: LineageDog) => {
    const base = template ? copyDog(template) : {
      id: 'new-dog',
      name: 'New dog',
      registeredName: '',
      title: 'Profile details pending',
      image: { src: '/images/about.jpg', alt: 'Placeholder image for a new dog profile' },
      description: 'Add this dog’s verified profile details and story here before publishing.',
      health: [],
      awards: [],
    } satisfies LineageDog;
    const dog = { ...base, id: uniqueDogId(base.id || base.name, content.lineage) };
    patch((current) => ({ ...current, lineage: [...current.lineage, dog] }));
    setSelectedDogId(dog.id);
    setDeleteDogId(null);
  };

  const addMissingNamedDogs = () => {
    if (!missingNamedDogs.length) return;
    const additions = missingNamedDogs.map(copyDog);
    patch((current) => ({ ...current, lineage: [...current.lineage, ...additions] }));
    setSelectedDogId(additions[0].id);
    setDeleteDogId(null);
  };

  const duplicateDog = (dog: LineageDog) => {
    const duplicate = { ...copyDog(dog), id: uniqueDogId(`${dog.id}-copy`, content.lineage), name: `${dog.name || 'Untitled dog'} copy` };
    patch((current) => {
      const index = current.lineage.findIndex((item) => item.id === dog.id);
      const lineage = [...current.lineage];
      lineage.splice(index + 1, 0, duplicate);
      return { ...current, lineage };
    });
    setSelectedDogId(duplicate.id);
    setDeleteDogId(null);
  };

  const reorderDog = (dogId: string, offset: -1 | 1) => {
    patch((current) => ({ ...current, lineage: moveDog(current.lineage, current.lineage.findIndex((dog) => dog.id === dogId), offset) }));
  };

  const removeDog = (dog: LineageDog) => {
    if (content.lineage.length <= 1) return;
    const index = content.lineage.findIndex((item) => item.id === dog.id);
    const nextSelected = content.lineage[index + 1] ?? content.lineage[index - 1];
    patch((current) => ({ ...current, lineage: current.lineage.filter((item) => item.id !== dog.id) }));
    setSelectedDogId(nextSelected?.id ?? '');
    setDeleteDogId(null);
  };

  const uploadDogImage = async (file: File, dogId: string) => {
    if (!file.type.startsWith('image/')) { setUploadNote('Choose a PNG, JPG, or WEBP image.'); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadNote('Images must be 10 MB or smaller.'); return; }
    setUploadNote('Uploading…');
    try {
      // Upload the file to our server, which streams it to R2 (credentials stay server-side, no CORS).
      const { id, deliveryURL } = await adminApi.uploadMedia(file, { dogId });
      // Point the dog's image at the public URL and mark the CMS dirty (Save to publish).
      const src = deliveryURL || id;
      patch((current) => ({ ...current, lineage: current.lineage.map((dog) => (dog.id === dogId ? { ...dog, image: { ...dog.image, src, crop: undefined } } : dog)) }));
      setUploadNote('Image set — Save to publish it.');
    } catch {
      setUploadNote('Image storage is not configured yet (see Settings).');
    }
  };

  const addFacebookItem = (kind: FacebookEmbed['kind']) => {
    patch((current) => {
      const baseId = `${kind}-${Date.now()}`;
      let id = baseId;
      let suffix = 2;
      while (current.facebook.items.some((item) => item.id === id)) {
        id = `${baseId}-${suffix}`;
        suffix += 1;
      }
      const item: FacebookEmbed = {
        id,
        kind,
        url: '',
        title: kind === 'video' ? 'New Facebook video' : 'New Facebook post',
      };
      return { ...current, facebook: { ...current.facebook, items: [...current.facebook.items, item] } };
    });
    setRemoveFacebookId(null);
  };

  const updateFacebookItem = (itemId: string, values: Partial<Omit<FacebookEmbed, 'id'>>) => {
    patch((current) => ({
      ...current,
      facebook: {
        ...current.facebook,
        items: current.facebook.items.map((item) => (item.id === itemId ? { ...item, ...values } : item)),
      },
    }));
  };

  const reorderFacebookItem = (itemId: string, offset: -1 | 1) => {
    patch((current) => {
      const index = current.facebook.items.findIndex((item) => item.id === itemId);
      const destination = index + offset;
      if (index < 0 || destination < 0 || destination >= current.facebook.items.length) return current;
      const items = [...current.facebook.items];
      [items[index], items[destination]] = [items[destination], items[index]];
      return { ...current, facebook: { ...current.facebook, items } };
    });
  };

  const removeFacebookItem = (itemId: string) => {
    patch((current) => ({
      ...current,
      facebook: { ...current.facebook, items: current.facebook.items.filter((item) => item.id !== itemId) },
    }));
    setRemoveFacebookId(null);
  };

  return (
    <div className="cms-page">
      <div className="cms-page-head cms-page-head-row">
        <div>
          <h1>Site content</h1>
          <p>Maintain reusable copy, dog profiles, health standards, and FAQs.</p>
        </div>
        <button type="button" className="cms-btn cms-btn-ghost" onClick={() => window.confirm('Replace the current content draft with the Sugarloaf defaults?') && resetContent()}>Reset to defaults</button>
      </div>

      <aside className="cms-design-callout">
        <span><Paintbrush size={18} /></span>
        <div><b>Design is the home of page composition</b><p>Use the visual editor for layout, visibility, theme settings, media, and every homepage section.</p></div>
        <a className="cms-btn cms-btn-primary" href="/admin/design">Open visual editor</a>
      </aside>

      <div className="cms-subnav">
        {SECTIONS.map(([id, label, anchor]) => (
          <div key={id} className="cms-subnav-item">
            <button type="button" className={`cms-chip ${section === id ? 'active' : ''}`} onClick={() => setSection(id)}>{label}</button>
            <a href={anchor} target="_blank" rel="noreferrer" className="cms-link cms-link-sm">View ↗</a>
          </div>
        ))}
      </div>

      {section === 'hero' && (
        <div className="cms-card">
          <label className="cms-field"><span>Eyebrow</span><input value={content.hero.eyebrow} onChange={(event) => patch((c) => ({ ...c, hero: { ...c.hero, eyebrow: event.target.value } }))} /></label>
          <label className="cms-field"><span>Headline</span><input value={content.hero.title} onChange={(event) => patch((c) => ({ ...c, hero: { ...c.hero, title: event.target.value } }))} /></label>
          <label className="cms-field"><span>Supporting text</span><textarea rows={3} value={content.hero.subtitle} onChange={(event) => patch((c) => ({ ...c, hero: { ...c.hero, subtitle: event.target.value } }))} /></label>
        </div>
      )}

      {section === 'copy' && (
        <div className="cms-card">
          {Object.entries(content.copy).map(([key, value]) => (
            <label key={key} className="cms-field">
              <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}</span>
              <textarea rows={/subtext|intro|blurb/i.test(key) ? 3 : 1} value={value} onChange={(event) => patch((c) => ({ ...c, copy: { ...c.copy, [key]: event.target.value } }))} />
            </label>
          ))}
        </div>
      )}

      {section === 'dogs' && (
        <section className="site-dogs-editor">
          <style>{DOG_EDITOR_STYLES}</style>
          <header className="site-dogs-toolbar">
            <div><h2>Dog profiles</h2><p>{content.lineage.length} profile{content.lineage.length === 1 ? '' : 's'} shown on the public site in this order.</p></div>
            <div>
              {missingNamedDogs.length > 0 && <button type="button" className="cms-btn cms-btn-ghost" onClick={addMissingNamedDogs}>Add named profiles ({missingNamedDogs.length})</button>}
              <button type="button" className="cms-btn cms-btn-primary" onClick={() => addDog()}>Add dog</button>
            </div>
          </header>

          {uploadNote && <p className="site-dogs-note" role="status">{uploadNote}</p>}

          <div className="site-dogs-layout">
            <aside className="site-dogs-list" aria-label="Dog profiles">
              {content.lineage.map((dog, index) => (
                <article key={dog.id} className={selectedDog?.id === dog.id ? 'is-selected' : ''}>
                  <button type="button" className="site-dogs-select" aria-label={`Edit ${dog.name || 'untitled dog'}`} aria-pressed={selectedDog?.id === dog.id} onClick={() => { setSelectedDogId(dog.id); setDeleteDogId(null); }}>
                    <img src={dog.image.src || '/images/about.jpg'} alt="" />
                    <span><strong>{dog.name || 'Untitled dog'}</strong><small>{dog.title || 'No title added'}</small></span>
                  </button>
                  <div className="site-dogs-row-actions">
                    <button type="button" aria-label={`Move ${dog.name} up`} title="Move up" disabled={index === 0} onClick={() => reorderDog(dog.id, -1)}>↑</button>
                    <button type="button" aria-label={`Move ${dog.name} down`} title="Move down" disabled={index === content.lineage.length - 1} onClick={() => reorderDog(dog.id, 1)}>↓</button>
                    <button type="button" aria-label={`Duplicate ${dog.name}`} title="Duplicate" onClick={() => duplicateDog(dog)}>⧉</button>
                  </div>
                </article>
              ))}
              {!content.lineage.length && <p className="cms-empty">No profiles yet. Add a dog to get started.</p>}
            </aside>

            {selectedDog ? (
              <article className="site-dogs-form" aria-label={`Editing ${selectedDog.name || 'untitled dog'}`}>
                <header>
                  <div><span className="cms-eyebrow">Profile {selectedDogIndex + 1}</span><h2>{selectedDog.name || 'Untitled dog'}</h2><code>{selectedDog.id}</code></div>
                  <div>
                    <button type="button" className="cms-btn cms-btn-ghost" onClick={() => duplicateDog(selectedDog)}>Duplicate</button>
                    <a className="cms-link" href="/#dogs" target="_blank" rel="noreferrer">View ↗</a>
                  </div>
                </header>

                <div className="site-dogs-fields">
                  <div className="cms-field-row">
                    <label className="cms-field"><span>Call name</span><input value={selectedDog.name} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, name: event.target.value }))} /></label>
                    <label className="cms-field"><span>Registered name</span><input value={selectedDog.registeredName} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, registeredName: event.target.value }))} /></label>
                  </div>
                  <label className="cms-field"><span>Title / role</span><input value={selectedDog.title} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, title: event.target.value }))} placeholder="Only add verified titles or a descriptive role" /></label>
                  <label className="cms-field"><span>Biography</span><textarea rows={4} value={selectedDog.description} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, description: event.target.value }))} /></label>

                  <section className="site-dogs-image">
                    <img src={selectedDog.image.src || '/images/about.jpg'} alt={selectedDog.image.alt || ''} />
                    <div>
                      <h3>Profile image</h3>
                      <p>Choose an existing image, upload to the media library, or enter a URL.</p>
                      <div className="site-dogs-image-actions">
                        <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setMediaDogId(selectedDog.id)}>Choose from library</button>
                        <label className="cms-btn cms-btn-ghost site-dogs-upload">Quick upload<input type="file" accept="image/*" aria-label={`Upload image for ${selectedDog.name}`} onChange={(event: ChangeEvent<HTMLInputElement>) => event.target.files?.[0] && uploadDogImage(event.target.files[0], selectedDog.id)} /></label>
                      </div>
                    </div>
                  </section>
                  <label className="cms-field"><span>Image URL</span><input value={selectedDog.image.src} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, image: { ...dog.image, src: event.target.value, crop: undefined } }))} /></label>
                  <label className="cms-field"><span>Image alt text</span><input value={selectedDog.image.alt} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, image: { ...dog.image, alt: event.target.value } }))} placeholder="Describe this specific photo" /></label>

                  <div className="site-dogs-two-col">
                    <label className="cms-field"><span>Health clearances</span><textarea rows={5} value={selectedDog.health.join('\n')} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, health: event.target.value.split('\n') }))} onBlur={() => updateDog(selectedDog.id, (dog) => ({ ...dog, health: cleanLines(dog.health) }))} /><small>One verified clearance per line. Leave blank until confirmed.</small></label>
                    <label className="cms-field"><span>Titles & awards</span><textarea rows={5} value={selectedDog.awards.join('\n')} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, awards: event.target.value.split('\n') }))} onBlur={() => updateDog(selectedDog.id, (dog) => ({ ...dog, awards: cleanLines(dog.awards) }))} /><small>One verified title or award per line.</small></label>
                  </div>
                  <label className="cms-field"><span>Photo display filter</span><input value={selectedDog.filter ?? ''} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, filter: event.target.value || undefined }))} placeholder="Optional CSS filter, e.g. grayscale(0.2)" /></label>

                  <section className="site-dogs-pedigree">
                    <label className="cms-checkbox"><input type="checkbox" checked={Boolean(selectedDog.pedigree)} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: event.target.checked ? (dog.pedigree ?? emptyPedigree()) : undefined }))} /> Include pedigree details</label>
                    {selectedDog.pedigree && (
                      <div className="site-dogs-pedigree-fields">
                        <label className="cms-field"><span>Sire</span><input value={selectedDog.pedigree.sire} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: { ...(dog.pedigree ?? emptyPedigree()), sire: event.target.value } }))} /></label>
                        <label className="cms-field"><span>Sire’s parents</span><textarea rows={3} value={selectedDog.pedigree.sireGrands.join('\n')} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: { ...(dog.pedigree ?? emptyPedigree()), sireGrands: event.target.value.split('\n') } }))} onBlur={() => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: { ...(dog.pedigree ?? emptyPedigree()), sireGrands: cleanLines(dog.pedigree?.sireGrands ?? []) } }))} /><small>One per line.</small></label>
                        <label className="cms-field"><span>Dam</span><input value={selectedDog.pedigree.dam} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: { ...(dog.pedigree ?? emptyPedigree()), dam: event.target.value } }))} /></label>
                        <label className="cms-field"><span>Dam’s parents</span><textarea rows={3} value={selectedDog.pedigree.damGrands.join('\n')} onChange={(event) => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: { ...(dog.pedigree ?? emptyPedigree()), damGrands: event.target.value.split('\n') } }))} onBlur={() => updateDog(selectedDog.id, (dog) => ({ ...dog, pedigree: { ...(dog.pedigree ?? emptyPedigree()), damGrands: cleanLines(dog.pedigree?.damGrands ?? []) } }))} /><small>One per line.</small></label>
                      </div>
                    )}
                  </section>

                  <footer className="site-dogs-danger">
                    {content.lineage.length === 1 ? <p>The public dogs section needs at least one profile.</p>
                      : deleteDogId === selectedDog.id ? (
                        <div><p>Delete “{selectedDog.name || 'Untitled dog'}”? This takes effect when you save.</p><button type="button" className="cms-btn cms-btn-danger" onClick={() => removeDog(selectedDog)}>Confirm delete</button><button type="button" className="cms-btn cms-btn-ghost" onClick={() => setDeleteDogId(null)}>Cancel</button></div>
                      ) : <button type="button" className="cms-btn cms-btn-danger" onClick={() => setDeleteDogId(selectedDog.id)}>Delete dog</button>}
                  </footer>
                </div>
              </article>
            ) : (
              <article className="site-dogs-form site-dogs-empty"><h2>Add the first dog profile</h2><p>Start with a safe placeholder and replace it with verified details before publishing.</p><button type="button" className="cms-btn cms-btn-primary" onClick={() => addDog()}>Add dog</button></article>
            )}
          </div>

          <MediaModal
            open={Boolean(mediaDogId)}
            dogId={mediaDogId ?? undefined}
            onClose={() => setMediaDogId(null)}
            onSelect={(url) => {
              if (mediaDogId) updateDog(mediaDogId, (dog) => ({ ...dog, image: { ...dog.image, src: url, crop: undefined } }));
              setUploadNote('Image selected — Save to publish it.');
            }}
          />
        </section>
      )}

      {section === 'facebook' && (
        <section className="site-facebook-editor">
          <style>{FACEBOOK_EDITOR_STYLES}</style>
          <header className="site-facebook-toolbar">
            <div>
              <span className="site-facebook-icon" aria-hidden>f</span>
              <div><h2>Facebook videos &amp; posts</h2><p>Curate public Sugarloaf updates without sharing Facebook credentials or SDK secrets.</p></div>
            </div>
            <label className="cms-checkbox"><input type="checkbox" checked={isFacebookSectionVisible(content)} onChange={(event) => patch((current) => setFacebookSectionVisibility(current, event.target.checked))} /> Show on public site</label>
          </header>

          <aside className="site-facebook-guide">
            <strong>Paste the permalink for each public Facebook video, reel, or post.</strong>
            <p>Open the item on Facebook, copy its full link, and paste it below. Private, friends-only, deleted, or age-restricted items may not embed; visitors will always have a direct Facebook link as a fallback.</p>
          </aside>

          <div className="cms-card site-facebook-copy">
            <SafeFacebookUrlInput
              label="Facebook page URL"
              value={content.facebook.pageUrl}
              placeholder={SUGARLOAF_FACEBOOK_PAGE_URL}
              help="Only secure facebook.com links are accepted. This page is used when no individual videos are listed."
              onCommit={(pageUrl) => patch((current) => ({ ...current, facebook: { ...current.facebook, pageUrl } }))}
            />
            <div className="cms-field-row">
              <label className="cms-field"><span>Eyebrow</span><input value={content.facebook.eyebrow} onChange={(event) => patch((current) => ({ ...current, facebook: { ...current.facebook, eyebrow: event.target.value } }))} /></label>
              <label className="cms-field cms-field-grow"><span>Section title</span><input value={content.facebook.title} onChange={(event) => patch((current) => ({ ...current, facebook: { ...current.facebook, title: event.target.value } }))} /></label>
            </div>
            <label className="cms-field"><span>Introduction</span><textarea rows={3} value={content.facebook.intro} onChange={(event) => patch((current) => ({ ...current, facebook: { ...current.facebook, intro: event.target.value } }))} /></label>
          </div>

          <div className="site-facebook-list-head">
            <div><h3>Featured updates</h3><p>{content.facebook.items.length} item{content.facebook.items.length === 1 ? '' : 's'} · shown in this order</p></div>
            <div><button type="button" className="cms-btn cms-btn-ghost" onClick={() => addFacebookItem('post')}>Add post</button><button type="button" className="cms-btn cms-btn-primary" onClick={() => addFacebookItem('video')}>Add video</button></div>
          </div>

          {content.facebook.items.length ? (
            <div className="site-facebook-list">
              {content.facebook.items.map((item, index) => (
                <article key={item.id} className="site-facebook-item" aria-label={`${item.kind === 'video' ? 'Facebook video' : 'Facebook post'} ${index + 1}`}>
                  <header>
                    <span>{index + 1}</span>
                    <div><strong>{item.title || `Untitled Facebook ${item.kind}`}</strong><small>{item.kind === 'video' ? 'Video or reel' : 'Post'}</small></div>
                    <div className="site-facebook-order">
                      <button type="button" aria-label={`Move ${item.title || `item ${index + 1}`} up`} disabled={index === 0} onClick={() => reorderFacebookItem(item.id, -1)}>↑</button>
                      <button type="button" aria-label={`Move ${item.title || `item ${index + 1}`} down`} disabled={index === content.facebook.items.length - 1} onClick={() => reorderFacebookItem(item.id, 1)}>↓</button>
                    </div>
                  </header>
                  <div className="site-facebook-item-fields">
                    <div className="cms-field-row">
                      <label className="cms-field"><span>Embed type</span><select value={item.kind} onChange={(event) => updateFacebookItem(item.id, { kind: event.target.value as FacebookEmbed['kind'] })}><option value="video">Video or reel</option><option value="post">Post</option></select></label>
                      <label className="cms-field cms-field-grow"><span>Display title</span><input value={item.title} onChange={(event) => updateFacebookItem(item.id, { title: event.target.value })} /></label>
                    </div>
                    <SafeFacebookUrlInput
                      label={`Facebook ${item.kind} URL`}
                      value={item.url}
                      placeholder={item.kind === 'video' ? 'https://www.facebook.com/.../videos/...' : 'https://www.facebook.com/.../posts/...'}
                      help="Use the full public permalink. The URL is embedded through Facebook’s official plugin."
                      onCommit={(url) => updateFacebookItem(item.id, { url })}
                    />
                    <footer>
                      {removeFacebookId === item.id ? (
                        <div className="site-facebook-confirm"><span>Remove this featured update when the draft is saved?</span><button type="button" className="cms-btn cms-btn-danger" onClick={() => removeFacebookItem(item.id)}>Confirm remove</button><button type="button" className="cms-btn cms-btn-ghost" onClick={() => setRemoveFacebookId(null)}>Cancel</button></div>
                      ) : <button type="button" className="cms-btn cms-btn-danger" onClick={() => setRemoveFacebookId(item.id)}>Remove item</button>}
                    </footer>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="site-facebook-empty"><span className="site-facebook-icon" aria-hidden>f</span><h3>No individual videos added yet</h3><p>The public section links to the exact Sugarloaf Facebook page until you add public video, reel, or post permalinks here.</p><button type="button" className="cms-btn cms-btn-primary" onClick={() => addFacebookItem('video')}>Add the first video</button></div>
          )}
        </section>
      )}

      {section === 'health' && (
        <div className="cms-cards">
          {content.healthStandards.map((standard, index) => (
            <article key={standard.code} className="cms-card">
              <div className="cms-field-row">
                <label className="cms-field"><span>Code</span><input value={standard.code} onChange={(event) => patch((c) => { const list = [...c.healthStandards]; list[index] = { ...standard, code: event.target.value }; return { ...c, healthStandards: list }; })} /></label>
                <label className="cms-field cms-field-grow"><span>Title</span><input value={standard.title} onChange={(event) => patch((c) => { const list = [...c.healthStandards]; list[index] = { ...standard, title: event.target.value }; return { ...c, healthStandards: list }; })} /></label>
              </div>
              <label className="cms-field"><span>Body</span><textarea rows={2} value={standard.body} onChange={(event) => patch((c) => { const list = [...c.healthStandards]; list[index] = { ...standard, body: event.target.value }; return { ...c, healthStandards: list }; })} /></label>
            </article>
          ))}
        </div>
      )}

      {section === 'faq' && (
        <div className="cms-cards">
          {content.faq.map((faq, index) => (
            <article key={index} className="cms-card">
              <label className="cms-field"><span>Question</span><input value={faq.question} onChange={(event) => patch((c) => { const list = [...c.faq]; list[index] = { ...faq, question: event.target.value }; return { ...c, faq: list }; })} /></label>
              <label className="cms-field"><span>Answer</span><textarea rows={3} value={faq.answer} onChange={(event) => patch((c) => { const list = [...c.faq]; list[index] = { ...faq, answer: event.target.value }; return { ...c, faq: list }; })} /></label>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function SafeFacebookUrlInput({ label, value, placeholder, help, onCommit }: { label: string; value: string; placeholder: string; help: string; onCommit: (value: string) => void }) {
  const inputId = useId();
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState('');

  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft('');
      setError('');
      onCommit('');
      return;
    }
    const safeUrl = normalizeFacebookUrl(trimmed);
    if (!safeUrl) {
      setError('Use a full, secure facebook.com URL. Other websites are not accepted.');
      return;
    }
    setDraft(safeUrl);
    setError('');
    onCommit(safeUrl);
  };

  const updateDraft = (next: string) => {
    setDraft(next);
    if (error) setError('');
    const trimmed = next.trim();
    if (!trimmed) {
      onCommit('');
      return;
    }
    const safeUrl = normalizeFacebookUrl(trimmed);
    if (safeUrl) onCommit(trimmed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commit();
  };

  return (
    <label className="cms-field site-facebook-url" htmlFor={inputId}>
      <span>{label}</span>
      <input
        id={inputId}
        aria-label={label}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : helpId}
        type="url"
        inputMode="url"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={draft}
        placeholder={placeholder}
        onChange={(event) => updateDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
      {error ? <small id={errorId} className="site-facebook-url-error" role="alert">{error}</small> : <small id={helpId}>{help}</small>}
    </label>
  );
}

const FACEBOOK_EDITOR_STYLES = `
  .site-facebook-editor { --fb-blue: #1877f2; --fb-line: var(--cms-line, #e7dfcc); --fb-muted: var(--cms-muted, #8a7d68); display: grid; gap: 14px; }
  .site-facebook-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 17px 19px; border: 1px solid var(--fb-line); border-radius: 14px; background: #fff; }
  .site-facebook-toolbar > div:first-child { display: flex; align-items: center; gap: 13px; }
  .site-facebook-toolbar h2, .site-facebook-list-head h3, .site-facebook-empty h3 { margin: 0; font-family: 'Bricolage Grotesque', sans-serif; }
  .site-facebook-toolbar h2 { font-size: 19px; }
  .site-facebook-toolbar p, .site-facebook-list-head p, .site-facebook-empty p { margin: 2px 0 0; color: var(--fb-muted); font-size: 13px; line-height: 1.45; }
  .site-facebook-icon { width: 39px; height: 39px; display: grid; place-items: end center; flex: 0 0 39px; overflow: hidden; border-radius: 11px; background: var(--fb-blue); color: #fff; font: 800 38px/34px Arial, sans-serif; }
  .site-facebook-guide { padding: 14px 17px; border: 1px solid #cfe0fb; border-radius: 12px; background: #f3f7ff; color: #263853; }
  .site-facebook-guide strong { display: block; margin-bottom: 3px; font-size: 13px; }
  .site-facebook-guide p { margin: 0; color: #50627b; font-size: 12.5px; line-height: 1.5; }
  .site-facebook-copy { display: grid; gap: 14px; }
  .site-facebook-url small { color: var(--fb-muted); font-size: 11.5px; line-height: 1.4; }
  .site-facebook-url input[aria-invalid='true'] { border-color: #b8473e; box-shadow: 0 0 0 2px rgba(184, 71, 62, .09); }
  .site-facebook-url .site-facebook-url-error { color: #a53730; font-weight: 700; }
  .site-facebook-list-head { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 4px 2px 0; }
  .site-facebook-list-head h3 { font-size: 16px; }
  .site-facebook-list-head > div:last-child { display: flex; gap: 8px; }
  .site-facebook-list { display: grid; gap: 11px; }
  .site-facebook-item { overflow: hidden; border: 1px solid var(--fb-line); border-radius: 13px; background: #fff; }
  .site-facebook-item > header { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 11px; padding: 12px 15px; border-bottom: 1px solid var(--fb-line); background: #fbfaf7; }
  .site-facebook-item > header > span { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 8px; background: #e8f1ff; color: #275ca8; font-size: 12px; font-weight: 900; }
  .site-facebook-item > header > div:nth-child(2) { min-width: 0; display: grid; gap: 1px; }
  .site-facebook-item > header strong, .site-facebook-item > header small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .site-facebook-item > header strong { font-size: 13.5px; }
  .site-facebook-item > header small { color: var(--fb-muted); font-size: 11px; }
  .site-facebook-order { display: flex; gap: 4px; }
  .site-facebook-order button { width: 31px; height: 29px; border: 1px solid var(--fb-line); border-radius: 8px; background: #fff; color: #716653; cursor: pointer; }
  .site-facebook-order button:disabled { opacity: .35; cursor: not-allowed; }
  .site-facebook-item-fields { display: grid; gap: 13px; padding: 15px; }
  .site-facebook-item-fields footer { padding-top: 2px; }
  .site-facebook-confirm { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 11px; border: 1px solid #efd0cd; border-radius: 10px; background: #fff7f6; }
  .site-facebook-confirm span { width: 100%; color: #7f3333; font-size: 12.5px; }
  .site-facebook-empty { display: grid; justify-items: center; gap: 7px; padding: 36px 24px; border: 1px dashed #b9cbea; border-radius: 14px; background: #f8faff; text-align: center; }
  .site-facebook-empty .site-facebook-icon { margin-bottom: 3px; }
  .site-facebook-empty p { max-width: 580px; margin-bottom: 8px; }
  @media (max-width: 680px) { .site-facebook-toolbar, .site-facebook-list-head { align-items: flex-start; flex-direction: column; } .site-facebook-toolbar > div:first-child { align-items: flex-start; } .site-facebook-list-head > div:last-child { width: 100%; } .site-facebook-list-head .cms-btn { flex: 1; } }
`;

const DOG_EDITOR_STYLES = `
  .site-dogs-editor { --dog-line: var(--cms-line, #e7dfcc); --dog-ink: var(--cms-ink, #2c2620); --dog-muted: var(--cms-muted, #8a7d68); --dog-panel: var(--cms-panel, #fff); display: grid; gap: 14px; }
  .site-dogs-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 18px; border: 1px solid var(--dog-line); border-radius: 14px; background: var(--dog-panel); }
  .site-dogs-toolbar h2 { margin: 0 0 2px; font-family: 'Bricolage Grotesque', serif; font-size: 18px; }
  .site-dogs-toolbar p { margin: 0; color: var(--dog-muted); font-size: 13px; }
  .site-dogs-toolbar > div:last-child { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
  .site-dogs-note { margin: 0; padding: 10px 13px; border: 1px solid #edd7b6; border-radius: 10px; background: #fbf0dd; color: #765326; font-size: 13px; font-weight: 700; }
  .site-dogs-layout { display: grid; grid-template-columns: 230px minmax(0, 1fr); gap: 14px; align-items: start; }
  .site-dogs-list { display: grid; gap: 7px; max-height: calc(100vh - 220px); overflow-y: auto; padding-right: 3px; }
  .site-dogs-list > article { overflow: hidden; border: 1px solid var(--dog-line); border-radius: 11px; background: var(--dog-panel); }
  .site-dogs-list > article.is-selected { border-color: var(--cms-orange, #de9445); box-shadow: 0 0 0 1px var(--cms-orange, #de9445) inset; }
  .site-dogs-select { width: 100%; min-width: 0; display: flex; align-items: center; gap: 10px; padding: 10px; border: 0; background: transparent; color: var(--dog-ink); text-align: left; cursor: pointer; }
  .site-dogs-select img { width: 46px; height: 46px; flex: 0 0 46px; border-radius: 9px; object-fit: cover; background: #f4edde; }
  .site-dogs-select span { min-width: 0; display: grid; gap: 2px; }
  .site-dogs-select strong, .site-dogs-select small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .site-dogs-select strong { font-size: 14px; }
  .site-dogs-select small { color: var(--dog-muted); font-size: 11.5px; }
  .site-dogs-row-actions { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--dog-line); }
  .site-dogs-row-actions button { min-height: 29px; border: 0; border-right: 1px solid var(--dog-line); background: #faf7f0; color: var(--dog-muted); cursor: pointer; }
  .site-dogs-row-actions button:last-child { border-right: 0; }
  .site-dogs-row-actions button:hover:not(:disabled) { background: #fbf0dd; color: var(--dog-ink); }
  .site-dogs-row-actions button:disabled { opacity: .35; cursor: not-allowed; }
  .site-dogs-form { overflow: hidden; border: 1px solid var(--dog-line); border-radius: 14px; background: var(--dog-panel); }
  .site-dogs-form > header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 17px 20px; border-bottom: 1px solid var(--dog-line); }
  .site-dogs-form > header h2 { margin: 0; font-family: 'Bricolage Grotesque', serif; font-size: 21px; }
  .site-dogs-form > header .cms-eyebrow { display: block; margin: 0 0 1px; }
  .site-dogs-form > header code { color: var(--dog-muted); font-size: 11px; }
  .site-dogs-form > header > div:last-child { display: flex; align-items: center; gap: 12px; }
  .site-dogs-fields { display: grid; gap: 15px; padding: 20px; }
  .site-dogs-fields .cms-field small { color: var(--dog-muted); font-size: 12px; line-height: 1.4; }
  .site-dogs-image { display: grid; grid-template-columns: 150px 1fr; gap: 16px; align-items: center; padding: 14px; border: 1px solid var(--dog-line); border-radius: 12px; background: #faf7f0; }
  .site-dogs-image > img { width: 150px; height: 126px; border-radius: 10px; object-fit: cover; background: #f4edde; }
  .site-dogs-image h3 { margin: 0 0 3px; font-size: 15px; }
  .site-dogs-image p { margin: 0 0 12px; color: var(--dog-muted); font-size: 12.5px; line-height: 1.4; }
  .site-dogs-image-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .site-dogs-upload { position: relative; overflow: hidden; cursor: pointer; }
  .site-dogs-upload input { position: absolute; width: 1px; height: 1px; opacity: 0; }
  .site-dogs-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .site-dogs-pedigree { display: grid; gap: 13px; padding: 15px; border: 1px solid var(--dog-line); border-radius: 12px; background: #faf7f0; }
  .site-dogs-pedigree-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
  .site-dogs-danger { padding-top: 15px; border-top: 1px solid var(--dog-line); }
  .site-dogs-danger > p { margin: 0; color: var(--dog-muted); font-size: 13px; }
  .site-dogs-danger > div { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 12px; border: 1px solid #efd0cd; border-radius: 10px; background: #fff7f6; }
  .site-dogs-danger > div p { width: 100%; margin: 0; color: #7f3333; font-size: 13px; }
  .site-dogs-empty { display: grid; justify-items: start; gap: 10px; padding: 28px; }
  .site-dogs-empty h2, .site-dogs-empty p { margin: 0; }
  .site-dogs-empty p { color: var(--dog-muted); }
  @media (max-width: 800px) { .site-dogs-layout, .site-dogs-two-col, .site-dogs-pedigree-fields { grid-template-columns: 1fr; } .site-dogs-list { max-height: none; } }
  @media (max-width: 560px) { .site-dogs-toolbar, .site-dogs-form > header { align-items: flex-start; flex-direction: column; } .site-dogs-image { grid-template-columns: 1fr; } .site-dogs-image > img { width: 100%; height: 180px; } }
`;
